<template>
    <div class="app-container">
        <div
            id="screen-reader-global-messages"
            class="sr-only"
            role="status"
            aria-atomic="true"
            :aria-live="ariaLiveMode">
            {{ activeScreenReaderMessage }}
        </div>
        <template v-if="isAuthenticated">
            <global-banner
                :is-impersonating="isImpersonating"
                :home-subscription="homeSubscription" />
            <div
                class="app-content"
                :class="{ 'app-content--framed': showNavItems }">
                <side-nav v-if="showNavItems" />
                <div
                    id="main-container"
                    :class="{ 'main-container--framed': showNavItems }">
                    <top-nav
                        v-if="showNavItems"
                        :is-impersonating="isImpersonating"
                        :home-subscription="homeSubscription" />
                    <vue-wrapper />
                </div>
            </div>
            <impersonation-switcher v-if="hasSuperAccess" />
        </template>
        <template v-else>
            <router-view />
        </template>
    </div>
</template>

<script>
import { useUserJourneyStore } from '@/stores/journeys-user-journey.store';
import { i18n } from '@/utils/i18n';
import { showCelebrationModalOnPendoReady } from '@/free/free.utils.js';
import { buildUnauthenticatedLoginRedirectRoute } from '@/vue-router/route-guards.utils';
import { shouldShowNavItems } from '@/vue-router/route.utils';
import {
    appFrameGutterLeft,
    appFrameInset,
    appShellBorderRadius,
    appShellBorderWidth,
    sideNavTopOffset,
} from '@/constants/nav';
import { watch, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import GlobalBanner from '@/global-banner/global-banner.vue';
import SideNav from '@/side-nav/side-nav.vue';
import VueWrapper from '@/vue-router/vue-wrapper.vue';
import TopNav from '@/top-nav/top-nav.vue';
import useDarkMode from '@/common/use/use-dark-mode/use-dark-mode';
import useContrast, { CONTRAST_VALUES } from '@/common/use/use-contrast/use-contrast';
import useAriaLiveRegistry from '@/common/use/use-aria-live-registry/use-aria-live-registry';
import { useHeadingScope } from '@pendo/components-vue3';
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia';

import { useSubscriptionStore } from '@/stores/subscription.store';
import { useAuthStore } from '@/stores/auth.store';
import { useUserSettingsStore } from '@/stores/user-settings.store';

const APPEARANCE_SETTING_NAME = 'appearancePreferences';

export default {
    name: 'App',
    i18n,
    components: {
        SideNav,
        VueWrapper,
        TopNav,
        GlobalBanner,
        ImpersonationSwitcher: defineAsyncComponent(() => import('@/super/impersonation-switcher/impersonation-switcher.vue')),
    },
    setup () {
        useHeadingScope({ level: 1 });

        const { initializeDarkMode } = useDarkMode();
        const {
            contrastPreference,
            setContrast,
            setContrastEnabled,
            initializeContrast,
            hasUnsavedContrast,
            setUnsavedContrast,
        } = useContrast();
        const { activeScreenReaderMessage, ariaLiveMode, clearScreenReaderQueue: clearQueue } = useAriaLiveRegistry();
        const route = useRoute();

        watch(() => route.fullPath, () => {
            clearQueue();
        });

        initializeDarkMode();
        initializeContrast();

        return {
            initializeDarkMode,
            contrastPreference,
            setContrast,
            setContrastEnabled,
            hasUnsavedContrast,
            setUnsavedContrast,
            activeScreenReaderMessage,
            ariaLiveMode,
            appFrameGutterLeftPx: `${appFrameGutterLeft}px`,
            appFrameInsetPx: `${appFrameInset}px`,
            appShellBorderRadiusPx: `${appShellBorderRadius}px`,
            appShellBorderWidthPx: `${appShellBorderWidth}px`,
            sideNavTopOffsetPx: `${sideNavTopOffset}px`,
        };
    },
    data () {
        return {
            removeCompletedLoadWatch: null,
            removeContrastSaveWatch: null,
        };
    },
    computed: {
        ...mapPiniaState(useAuthStore, {
            hasCompletedUserLoad: 'hasCompletedUserLoad',
            isAuthenticated: 'isAuthenticated',
            isImpersonating: 'isImpersonating',
            hasSuperAccess: 'hasSuperAccess',
            hasOnlyTrainingSub: 'hasOnlyTrainingSub',
            hasOnlyNovusSub: 'hasOnlyNovusSub',
            hasSegmentFlag: 'hasSegmentFlag',
        }),
        ...mapPiniaState(useUserSettingsStore, {
            userSettingByName: 'settingByName',
            userSettingsError: 'error',
        }),
        ...mapPiniaState(useSubscriptionStore, {
            isAutoProvisionedTrialExpired: 'isAutoProvisionedTrialExpired',
            isPendoFree: 'isPendoFree',
            getFeatureFlag: 'getFeatureFlag',
            subscriptionList: 'list',
        }),
        ...mapPiniaState(useSubscriptionStore, {
            activeSubscription: (store) => store.active,
        }),
        homeSubscription () {
            if (!this.subscriptionList?.length) return null;

            return this.subscriptionList[0];
        },
        isHighContrastFlagOn () {
            return this.hasSegmentFlag('highContrastMode');
        },
        eventCount () {
            return this.activeSubscription?.eventCount || 0;
        },
        showNavItems () {
            return shouldShowNavItems(this.$route, this.isAuthenticated);
        },
    },
    watch: {
        isHighContrastFlagOn: {
            immediate: true,
            handler (isOn) {
                this.setContrastEnabled(isOn);
            },
        },
    },
    mounted () {
        this.scrollToHashAnchor();
        this.removeCompletedLoadWatch = this.$watch(
            () => this.hasCompletedUserLoad,
            this.onCompletedUserLoad.bind(this),
            { immediate: true },
        );
    },
    methods: {
        ...mapPiniaActions(useSubscriptionStore, {
            updateEventCount: 'updateEventCount',
            updateSetting: 'updateSetting',
        }),
        ...mapPiniaActions(useUserSettingsStore, {
            loadUserSettings: 'loadAll',
            updateUserSetting: 'update',
        }),
        async syncContrastPreference () {
            const preferenceBeforeLoad = this.contrastPreference;
            const errorBeforeLoad = this.userSettingsError;

            await this.loadUserSettings();

            const hasChangedSinceStart = this.contrastPreference !== preferenceBeforeLoad;
            // the store keeps a stale error after a later successful load, so only a new error means this load failed
            const didLoadFail = this.userSettingsError !== errorBeforeLoad;

            if (didLoadFail) {
                if (hasChangedSinceStart) this.setUnsavedContrast(this.$activeUser?.id);
            } else if (hasChangedSinceStart || this.hasUnsavedContrast(this.$activeUser?.id)) {
                // a choice made while settings loaded, or one whose save failed earlier, wins over the server value
                this.saveContrastPreference(this.contrastPreference);
            } else {
                const saved = this.userSettingByName(APPEARANCE_SETTING_NAME)?.value?.contrast;
                if (CONTRAST_VALUES.includes(saved)) this.setContrast(saved);
            }

            this.removeContrastSaveWatch ??= this.$watch(() => this.contrastPreference, this.saveContrastPreference);
        },
        saveContrastPreference (contrast) {
            if (this.isImpersonating || !this.isHighContrastFlagOn) return;

            const existing = this.userSettingByName(APPEARANCE_SETTING_NAME)?.value;

            this.setUnsavedContrast(this.$activeUser?.id);
            this.updateUserSetting({
                name: APPEARANCE_SETTING_NAME,
                value: { ...(existing && typeof existing === 'object' ? existing : {}), contrast },
            }).then(() => {
                if (this.contrastPreference === contrast) this.setUnsavedContrast(null);
            }).catch(() => {});
        },
        initUserJourney () {
            useUserJourneyStore().init();
        },
        scrollToHashAnchor () {
            const anchor = window.location.hash;
            if (anchor) {
                const anchorElement = document.querySelector(anchor);
                if (anchorElement) anchorElement.scrollIntoView();
            }
        },
        onCompletedUserLoad (hasCompletedLoading) {
            if (!hasCompletedLoading) return;

            if (!this.isAuthenticated) {
                if (this.$route?.meta?.skipLoginRedirect) return;

                const loginRoute = buildUnauthenticatedLoginRedirectRoute();
                this.$router.replace(loginRoute);

                return;
            }

            this.syncContrastPreference();

            if (this.hasOnlyNovusSub) {
                this.$router.replace({ name: 'NovusOnly' });

                return;
            }

            if (this.hasOnlyTrainingSub) {
                this.$router.replace({ name: 'Inactive' });

                return;
            }

            if (this.isAutoProvisionedTrialExpired) {
                this.$router.replace({ name: 'TrialExpired' });

                return;
            }

            if (!this.hasSuperAccess && !this.getFeatureFlag('allowSignedMetadata')) {
                const noEvents = this.eventCount === 0;
                const canBypassLockScreen = this.getFeatureFlag('bypassLockScreen');

                if (noEvents && !canBypassLockScreen) {
                    const { setupKey } = this.activeSubscription;

                    return {
                        name: 'Setup',
                        query: {
                            setupKey,
                        },
                    };
                }
            }

            if (this.$route.name === 'TrialExpired') {
                this.$router.replace({ name: 'Home' });

                return;
            }

            if (this.isPendoFree && this.eventCount === 0) {
                this.pollForInstallSuccess();
            }

            // because we eager watch for auth loading, sometimes its already done the first
            // time this watch gets called. In that case, this.removeCompletedLoadWatch hasn't
            // actually been set yet, so queue the unwatch
            this.$nextTick(() => {
                this.removeCompletedLoadWatch();
                delete this.removeCompletedLoadWatch;
            });
        },
        pollForInstallSuccess () {
            if (this.eventCount > 0) {
                showCelebrationModalOnPendoReady(() => {
                    this.updateSetting({
                        setting: 'seenCelebrationModal',
                        value: 'true',
                        label: 'seenCelebrationModal',
                    });
                });

                this.initUserJourney();
            } else {
                setTimeout(async () => {
                    await this.updateEventCount();
                    this.pollForInstallSuccess();
                }, 60000);
            }
        },
    },
};
</script>

<style lang="less" scoped>
.app-content {
    display: flex;
    position: relative;
    overflow: hidden;
    flex-grow: 1;

    &--framed {
        --app-frame-gutter-left: v-bind(appFrameGutterLeftPx);
        --app-frame-inset: v-bind(appFrameInsetPx);
        --app-shell-border-radius: v-bind(appShellBorderRadiusPx);
        --app-shell-border-width: v-bind(appShellBorderWidthPx);
        --side-nav-top-offset: v-bind(sideNavTopOffsetPx);
        background-color: var(--surface-secondary-background);

        #main-container {
            flex: 1 1 auto;
            min-width: 0;
            width: auto;
            margin: var(--app-frame-inset) var(--app-frame-inset) var(--app-frame-inset) var(--app-frame-gutter-left);
            border: var(--app-shell-border-width) solid var(--surface-default-border);
            border-radius: var(--app-shell-border-radius);
            height: auto;
            min-height: 0;
        }

        .side-nav + #main-container {
            margin-left: 0;
        }

        .screen-size(@zero, @large, {
            .side-nav + #main-container {
                margin-left: var(--app-frame-inset);
            }
        });
    }
}
</style>
